import React, { useEffect, useState } from "react";
import { ChevronRight, Plus, Save, Trash2, Users } from "lucide-react";
import { toast } from "react-toastify";
import API from "../utils/api";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";

const PAGE_STYLES = `
  .st-page {
    --st-bg: #f5f7fb; --st-card: #fff; --st-soft: #f8fafc; --st-text: #172033;
    --st-muted: #667085; --st-border: #e6eaf0; --st-blue: #2563eb; --st-blue-soft: #eff6ff;
    --st-red: #c2414b; min-height: 100%; padding: 28px; background: var(--st-bg); color: var(--st-text);
    font-family: "DM Sans", Inter, system-ui, sans-serif;
  }
  .st-page * { box-sizing: border-box; }
  .st-shell { width: 100%; max-width: 1100px; margin: 0 auto; }
  .st-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
  .st-eyebrow { display: block; margin-bottom: 5px; color: var(--st-blue); font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
  .st-header h1 { margin: 0 0 6px; font-size: 28px; line-height: 1.15; letter-spacing: -.03em; }
  .st-header p { max-width: 680px; margin: 0; color: var(--st-muted); font-size: 14px; line-height: 1.55; }
  .st-save { display: inline-flex; align-items: center; gap: 8px; min-height: 42px; padding: 10px 17px; border: 0; border-radius: 10px; background: var(--st-blue); color: #fff; box-shadow: 0 7px 16px rgb(37 99 235 / 22%); font: inherit; font-size: 14px; font-weight: 700; cursor: pointer; }
  .st-save:disabled { cursor: not-allowed; opacity: .55; }
  .st-layout { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 20px; align-items: start; }
  .st-sidebar, .st-card { border: 1px solid var(--st-border); border-radius: 14px; background: var(--st-card); box-shadow: 0 8px 28px rgb(23 32 51 / 5%); }
  .st-sidebar-head, .st-card-head { padding: 16px 18px; border-bottom: 1px solid var(--st-border); }
  .st-card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .st-sidebar-head strong, .st-card h2 { display: block; margin: 0; font-size: 15px; }
  .st-sidebar-head span, .st-help { display: block; margin-top: 4px; color: var(--st-muted); font-size: 12px; line-height: 1.4; }
  .st-new, .st-add { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 36px; border: 1px solid #bfdbfe; border-radius: 9px; background: var(--st-blue-soft); color: var(--st-blue); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
  .st-new { width: 100%; margin-top: 12px; }
  .st-add { padding: 0 12px; }
  .st-new:disabled, .st-add:disabled { cursor: not-allowed; opacity: .5; }
  .st-team-list { padding: 7px; }
  .st-team-option { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; padding: 11px 10px; border: 0; border-radius: 9px; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
  .st-team-option:hover { background: var(--st-soft); }
  .st-team-option.is-selected { background: var(--st-blue-soft); color: #1649aa; }
  .st-team-option strong, .st-team-option small { display: block; }
  .st-team-option strong { font-size: 13px; }
  .st-team-option small { margin-top: 3px; color: var(--st-muted); font-size: 11px; }
  .st-main { display: grid; gap: 16px; }
  .st-card-body { padding: 18px; }
  .st-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .st-field label { display: block; margin-bottom: 7px; color: #344054; font-size: 12px; font-weight: 700; }
  .st-field input, .st-field select, .st-add-row select { width: 100%; min-height: 40px; padding: 9px 11px; border: 1px solid #d8dee8; border-radius: 9px; background: #fff; color: inherit; font: inherit; font-size: 14px; }
  .st-field input:focus, .st-field select:focus, .st-add-row select:focus { outline: none; border-color: var(--st-blue); box-shadow: 0 0 0 3px rgb(37 99 235 / 12%); }
  .st-field input:disabled, .st-field select:disabled, .st-add-row select:disabled { background: #f2f4f7; color: #98a2b3; }
  .st-table { width: 100%; border-collapse: collapse; }
  .st-table th { padding: 10px 12px; background: var(--st-soft); color: var(--st-muted); font-size: 11px; font-weight: 700; letter-spacing: .04em; text-align: left; text-transform: uppercase; }
  .st-table td { padding: 10px 12px; border-top: 1px solid var(--st-border); font-size: 14px; }
  .st-icon-button { display: grid; width: 32px; height: 32px; place-items: center; border: 0; border-radius: 8px; background: transparent; color: var(--st-muted); cursor: pointer; }
  .st-icon-button:hover { background: #fff1f2; color: var(--st-red); }
  .st-add-row { display: grid; gap: 8px; padding: 12px; border-top: 1px solid var(--st-border); }
  .st-member-sub { display: block; margin-top: 2px; color: var(--st-muted); font-size: 12px; }
  .st-empty { padding: 22px; color: var(--st-muted); text-align: center; }
  .st-danger { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 18px; border: 1px solid #fecdd3; border-radius: 14px; background: #fffafb; }
  .st-danger strong { color: #9f2733; font-size: 13px; }
  .st-danger span { display: block; margin-top: 3px; color: #8d5960; font-size: 12px; }
  .st-delete { display: inline-flex; align-items: center; gap: 7px; min-height: 36px; padding: 8px 12px; border: 1px solid #fecdd3; border-radius: 9px; background: #fff; color: var(--st-red); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
  @media (max-width: 800px) {
    .st-page { padding: 16px; }
    .st-layout, .st-fields { grid-template-columns: 1fr; }
    .st-danger { align-items: flex-start; flex-direction: column; }
  }
`;

function displayNumber(value) {
  if (value == null || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : "";
}

function blankTeam() {
  return {
    id: null,
    name: "",
    leaderId: "",
    overrideAmount: "",
    overrideMultiple: "",
    members: [],
  };
}

function teamFromApi(team) {
  return {
    id: team.id,
    name: team.name || "",
    leaderId: team.leader_id ? String(team.leader_id) : "",
    overrideAmount: displayNumber(team.override_amount),
    overrideMultiple: displayNumber(team.override_multiple),
    members: (team.members || []).map((member) => ({ id: member.id, name: member.name })),
  };
}

export default function SalesTeamsPage() {
  const { isReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [draft, setDraft] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const applyList = (data, selectId) => {
    const nextTeams = data.teams || [];
    setPeople(data.people || []);
    setTeams(nextTeams);
    const chosen = nextTeams.find((team) => team.id === selectId) || nextTeams[0];
    setDraft(chosen ? teamFromApi(chosen) : null);
  };

  useEffect(() => {
    let cancelled = false;
    API.get("settings/sales-teams/")
      .then((res) => {
        if (!cancelled) applyList(res.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load sales teams.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const otherTeams = teams.filter((team) => team.id !== draft?.id);
  const memberElsewhere = new Set(otherTeams.flatMap((team) => (team.members || []).map((member) => member.id)));
  const leadersElsewhere = new Set(otherTeams.map((team) => team.leader_id).filter(Boolean));
  const leaderOptions = people.filter(
    (person) => !leadersElsewhere.has(person.id) || String(person.id) === draft?.leaderId
  );
  const addOptions = people.filter(
    (person) =>
      String(person.id) !== draft?.leaderId &&
      !draft?.members.some((member) => member.id === person.id) &&
      !memberElsewhere.has(person.id) &&
      !leadersElsewhere.has(person.id)
  );
  const teamOptions = otherTeams.filter((team) => {
    if (!team.leader_id) return false;
    if (String(team.leader_id) === String(draft?.leaderId)) return false;
    if (draft?.members.some((member) => member.id === team.leader_id)) return false;
    if (memberElsewhere.has(team.leader_id)) return false;
    return !(team.members || []).some((member) => String(member.id) === String(draft?.leaderId));
  });

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        leader_id: draft.leaderId ? Number(draft.leaderId) : null,
        override_amount: draft.overrideAmount === "" ? null : draft.overrideAmount,
        override_multiple: draft.overrideMultiple === "" ? null : draft.overrideMultiple,
        member_ids: draft.members.map((member) => member.id),
      };
      const res = draft.id
        ? await API.put(`settings/sales-teams/${draft.id}/`, body)
        : await API.post("settings/sales-teams/", body);
      const list = await API.get("settings/sales-teams/");
      applyList(list.data, res.data.id);
      toast.success("Sales team saved.");
    } catch (err) {
      const data = err?.response?.data;
      const detail = data?.detail;
      const fieldMessage = data && typeof data === "object"
        ? Object.values(data).flat().find((value) => typeof value === "string")
        : null;
      toast.error(typeof detail === "string" ? detail : fieldMessage || "Couldn't save the sales team.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      await API.delete(`settings/sales-teams/${draft.id}/`);
      const list = await API.get("settings/sales-teams/");
      applyList(list.data);
      toast.success("Sales team deleted.");
    } catch {
      toast.error("Couldn't delete the sales team.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="st-page" aria-labelledby="sales-teams-title">
      <style>{PAGE_STYLES}</style>
      <div className="st-shell">
        <header className="st-header">
          <div>
            <span className="st-eyebrow">Commission</span>
            <h1 id="sales-teams-title">Sales Teams</h1>
            <p>Assign who leads each team, who is on it, and the leader override.</p>
          </div>
          <DisabledButtonWrapper
            isDisabled={isReadOnly || saving || loading || !draft}
            tooltipText={saving ? "Saving in progress..." : "Read-only mode: This action is disabled"}
          >
            <button type="button" className="st-save" onClick={handleSave} disabled={isReadOnly || saving || loading || !draft}>
              <Save size={16} aria-hidden="true" />
              Save team
            </button>
          </DisabledButtonWrapper>
        </header>

        {loading ? (
          <div className="st-empty">Loading sales teams…</div>
        ) : (
          <div className="st-layout">
            <aside className="st-sidebar" aria-label="Sales teams">
              <div className="st-sidebar-head">
                <strong>Teams</strong>
                <span>{teams.length} saved {teams.length === 1 ? "team" : "teams"}</span>
                <button type="button" className="st-new" onClick={() => setDraft(blankTeam())} disabled={isReadOnly}>
                  <Plus size={15} aria-hidden="true" />
                  New team
                </button>
              </div>
              <div className="st-team-list">
                {teams.length === 0 ? (
                  <div className="st-empty">No teams yet.</div>
                ) : (
                  teams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      className={`st-team-option${draft?.id === team.id ? " is-selected" : ""}`}
                      onClick={() => setDraft(teamFromApi(team))}
                    >
                      <span>
                        <strong>{team.name}</strong>
                        <small>{team.leader_name ? `Leader: ${team.leader_name}` : "No leader"}</small>
                      </span>
                      <ChevronRight size={15} aria-hidden="true" />
                    </button>
                  ))
                )}
              </div>
            </aside>

            <main className="st-main">
              {!draft ? (
                <div className="st-card"><div className="st-empty">Create a team to choose a leader and the people on it.</div></div>
              ) : (
                <>
                  <section className="st-card">
                    <header className="st-card-head"><h2>Team</h2></header>
                    <div className="st-card-body">
                      <div className="st-fields">
                        <div className="st-field">
                          <label htmlFor="sales-team-name">Team name</label>
                          <input
                            id="sales-team-name"
                            value={draft.name}
                            disabled={isReadOnly}
                            placeholder="Director team"
                            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                          />
                        </div>
                        <div className="st-field">
                          <label htmlFor="sales-team-leader">Leader</label>
                          <select
                            id="sales-team-leader"
                            value={draft.leaderId}
                            disabled={isReadOnly}
                            onChange={(event) => {
                              const leaderId = event.target.value;
                              setDraft({
                                ...draft,
                                leaderId,
                                members: draft.members.filter((member) => String(member.id) !== leaderId),
                              });
                            }}
                          >
                            <option value="">Choose a person</option>
                            {leaderOptions.map((person) => (
                              <option key={person.id} value={String(person.id)}>{person.name}</option>
                            ))}
                          </select>
                          <span className="st-help">One person. Their own sales pay from the commission scale on their profile.</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="st-card">
                    <header className="st-card-head">
                      <div>
                        <h2>Leader override</h2>
                        <span className="st-help">Paid on sales from people on this team. Leave blank for no override.</span>
                      </div>
                    </header>
                    <div className="st-card-body">
                      <div className="st-fields">
                        <div className="st-field">
                          <label htmlFor="sales-team-amount">Fixed amount</label>
                          <input
                            id="sales-team-amount"
                            value={draft.overrideAmount}
                            disabled={isReadOnly}
                            inputMode="decimal"
                            placeholder="25.00"
                            onChange={(event) => setDraft({ ...draft, overrideAmount: event.target.value })}
                          />
                        </div>
                        <div className="st-field">
                          <label htmlFor="sales-team-multiple">Multiple</label>
                          <input
                            id="sales-team-multiple"
                            value={draft.overrideMultiple}
                            disabled={isReadOnly}
                            inputMode="decimal"
                            placeholder="1.5"
                            onChange={(event) => setDraft({ ...draft, overrideMultiple: event.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="st-card">
                    <header className="st-card-head">
                      <div>
                        <h2>On this team</h2>
                        <span className="st-help">Add a person, or add a whole team under this leader.</span>
                      </div>
                      <Users size={18} aria-hidden="true" color="#2563eb" />
                    </header>
                    <table className="st-table">
                      <thead>
                        <tr>
                          <th>Person or team</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {draft.members.length === 0 ? (
                          <tr><td className="st-empty" colSpan={2}>No one on this team yet.</td></tr>
                        ) : (
                          draft.members.map((member) => {
                            const nestedTeam = otherTeams.find((team) => team.leader_id === member.id);
                            const label = nestedTeam?.name || member.name;
                            return (
                            <tr key={member.id}>
                              <td>
                                {label}
                                {nestedTeam ? (
                                  <>
                                    <span className="st-member-sub">Leader: {nestedTeam.leader_name}</span>
                                    {(nestedTeam.members || []).length > 0 ? (
                                      <span className="st-member-sub">
                                        {(nestedTeam.members || []).map((person) => person.name).join(", ")}
                                      </span>
                                    ) : null}
                                  </>
                                ) : null}
                              </td>
                              <td align="right">
                                <button
                                  type="button"
                                  className="st-icon-button"
                                  aria-label={`Remove ${label}`}
                                  disabled={isReadOnly}
                                  onClick={() =>
                                    setDraft({
                                      ...draft,
                                      members: draft.members.filter((row) => row.id !== member.id),
                                    })
                                  }
                                >
                                  <Trash2 size={15} aria-hidden="true" />
                                </button>
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                    <div className="st-add-row">
                      <select
                        aria-label="Add a person"
                        value=""
                        disabled={isReadOnly || addOptions.length === 0}
                        onChange={(event) => {
                          const person = people.find((row) => String(row.id) === event.target.value);
                          if (!person) return;
                          setDraft({ ...draft, members: [...draft.members, person] });
                        }}
                      >
                        <option value="">Add a person not already on a team</option>
                        {addOptions.map((person) => (
                          <option key={person.id} value={String(person.id)}>{person.name}</option>
                        ))}
                      </select>
                      <select
                        aria-label="Add a team"
                        value=""
                        disabled={isReadOnly || teamOptions.length === 0}
                        onChange={(event) => {
                          const team = teamOptions.find((row) => String(row.id) === event.target.value);
                          if (!team?.leader_id) return;
                          setDraft({
                            ...draft,
                            members: [
                              ...draft.members,
                              { id: team.leader_id, name: team.leader_name || team.name },
                            ],
                          });
                        }}
                      >
                        <option value="">Add a team</option>
                        {teamOptions.map((team) => (
                          <option key={team.id} value={String(team.id)}>
                            {team.name}{team.leader_name ? ` · ${team.leader_name}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </section>

                  <section className="st-danger">
                    <div>
                      <strong>Delete this sales team</strong>
                      <span>People on it will no longer be on a team. Their commission scales stay on their profiles.</span>
                    </div>
                    <button
                      type="button"
                      className="st-delete"
                      disabled={isReadOnly || saving || !draft.id}
                      onClick={() => {
                        if (!draft.id) {
                          setDraft(teams[0] ? teamFromApi(teams[0]) : null);
                          return;
                        }
                        setConfirmDeleteOpen(true);
                      }}
                    >
                      <Trash2 size={15} aria-hidden="true" />
                      Delete
                    </button>
                  </section>
                </>
              )}
            </main>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete sales team"
        message="Are you sure you want to delete this sales team? This action cannot be undone."
        itemName={draft?.name}
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
      />
    </section>
  );
}
